import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { appToast } from '../../../components/AppToast'
import { appConfirm } from '../../../components/AppConfirmModal'
import AppFormFooterBar from '../../../components/AppFormFooterBar'
import { fetchSolution, uploadSolution, deleteSolution } from './api/testApi'

const TIPS = [
  'Use this option to upload Solutions/Model answers for the test',
  'Only PDF files can be uploaded as solution.',
  'Students or theirs parents can log-in to WebEdify Student\'s Cloud to view/download solutions',
  'Solutions can be uploaded anytime, even before the test is conducted. However the solution is hidden from students till end of the test. It becomes visible to students / parents only after expiry of test schedule.',
]

export default function TestSolution() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const testId = searchParams.get('id')

  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [hasSolution, setHasSolution] = useState(false)
  const [fileName, setFileName] = useState('')
  const [fileUrl, setFileUrl] = useState('')
  const [selectedFile, setSelectedFile] = useState(null)

  const loadData = useCallback(async () => {
    if (!testId) {
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    try {
      const data = await fetchSolution(Number(testId))
      setHasSolution(Boolean(data.hasSolution))
      setFileName(data.fileName || '')
      setFileUrl(data.fileUrl || '')
    } catch (err) {
      appToast.error(err?.message || 'Failed to load test solution details.')
    } finally {
      setIsLoading(false)
    }
  }, [testId])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      appToast.error('Only PDF files can be uploaded as solution.')
      return
    }
    setSelectedFile(file)
    setFileName(file.name)
  }

  const handleDelete = async () => {
    const confirmed = await appConfirm({
      title: 'Delete Test Solution',
      message: 'Are you sure you want to delete this Solution PDF file?',
      confirmText: 'Delete',
      variant: 'danger',
    })
    if (!confirmed) return
    setIsSaving(true)
    try {
      await deleteSolution(Number(testId))
      setHasSolution(false)
      setFileName('')
      setFileUrl('')
      setSelectedFile(null)
      appToast.success('Test solution deleted successfully.')
    } catch (err) {
      appToast.error(err?.message || 'Failed to delete solution.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleSubmit = async () => {
    if (!selectedFile && !hasSolution) {
      appToast.warning('Please select a PDF file as solution to this test.')
      return
    }

    if (selectedFile) {
      setIsSaving(true)
      try {
        const res = await uploadSolution(Number(testId), selectedFile)
        setHasSolution(true)
        setFileName(res.fileName || selectedFile.name)
        setFileUrl(res.fileUrl || '')
        setSelectedFile(null)
        appToast.success('Solution uploaded successfully.')
        navigate('/education/tests')
      } catch (err) {
        appToast.error(err?.message || 'Failed to upload solution.')
      } finally {
        setIsSaving(false)
      }
    } else {
      navigate('/education/tests')
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-24">
      {/* ── Page Header (Rule 87: Canopy Hierarchy - Back button to parent tests list) ── */}
      <section className="flex flex-col gap-4 border-b border-outline-variant/10 pb-5">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/education/tests')}
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-outline-variant/30 bg-surface-container-lowest text-on-surface hover:bg-surface-container-high transition-colors cursor-pointer"
            title="Back to Tests"
          >
            <span className="material-symbols-outlined text-xl">arrow_back</span>
          </button>
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-on-surface">
              Solution
            </h1>
            <p className="mt-1 text-xs text-on-surface-variant">
              Upload solution and explanation documents for students to review after test conclusion.
            </p>
          </div>
        </div>
      </section>

      {isLoading ? (
        <div className="rounded-3xl border border-outline-variant/25 bg-surface-container-lowest p-16 text-center text-sm font-medium text-on-surface-variant">
          Loading solution details...
        </div>
      ) : (
        /* ── Main Content Grid (Matching Image 2) ── */
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* ── Left Column: Upload Section ── */}
          <div className="lg:col-span-8 flex flex-col gap-6">
            <div className="rounded-3xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm flex flex-col gap-5">
              {/* Notice Banner (Matching Image 2) */}
              <div className="rounded-2xl border border-outline-variant/25 bg-surface-container-low/60 p-4">
                {hasSolution ? (
                  <div className="flex flex-col gap-2">
                    <p className="text-xs text-on-surface">
                      This test already has a solution attached.{' '}
                      {fileUrl ? (
                        <a
                          href={fileUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="font-bold text-primary underline hover:text-primary/80"
                        >
                          Click here to view
                        </a>
                      ) : (
                        <span className="font-semibold text-primary">{fileName}</span>
                      )}
                      {' or select another file to replace'}
                    </p>
                    <div>
                      <button
                        type="button"
                        onClick={handleDelete}
                        className="text-xs font-bold text-error underline hover:text-error/80 cursor-pointer"
                      >
                        Click here to Delete
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-on-surface font-medium">
                    Please select a PDF file as solution to this test
                  </p>
                )}
              </div>

              {/* File input box matching Image 2 */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <input
                  type="text"
                  readOnly
                  value={selectedFile ? selectedFile.name : fileName || ''}
                  placeholder="Select a PDF file..."
                  className="flex-1 rounded-xl border border-outline-variant/30 bg-surface px-4 py-2.5 text-xs text-on-surface placeholder:text-on-surface-variant/40 focus:outline-none"
                />
                <label className="inline-flex items-center justify-center gap-2 rounded-xl border border-outline-variant/40 bg-surface-container-high px-4 py-2.5 text-xs font-bold text-on-surface hover:bg-surface-container-highest cursor-pointer transition-colors shrink-0">
                  <span className="material-symbols-outlined text-base">upload_file</span>
                  Select File
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    className="sr-only"
                  />
                </label>
              </div>
            </div>
          </div>

          {/* ── Right Column: TIPS Box (Matching Image 2) ── */}
          <div className="lg:col-span-4 flex flex-col gap-6">
            <section className="rounded-3xl border border-outline-variant/20 bg-slate-50/80 p-6 sticky top-6 shadow-sm">
              <h4 className="text-xs font-bold uppercase tracking-wider text-on-surface mb-3 flex items-center gap-2">
                <span className="material-symbols-outlined text-base text-primary">lightbulb</span>
                Tip
              </h4>
              <ul className="space-y-3 text-xs text-on-surface-variant leading-relaxed">
                {TIPS.map((tip, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-primary font-bold mt-0.5">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </div>
      )}

      {/* ── Bottom Form Footer Bar (Matching Image 2) ── */}
      <AppFormFooterBar
        cancelPath="/education/tests"
        onSaveExit={handleSubmit}
        saveLabel="Submit"
        isSaving={isSaving}
      />
    </div>
  )
}
